import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { AppIcon } from '@/components/AppIcon';
import { toast } from '@/stores/toast';
import { useConnection } from '@/stores/connection';
import { NOT_ON_THIS_PC } from '@/offline/availability';
import { readStored, visibleActions, writeStored, type DesktopAction } from './types';

function copyText(text: string): void {
  void navigator.clipboard?.writeText(text).then(
    () => toast.success('Copied'),
    () => toast.error('Could not copy'),
  );
}

export interface GridColumn<T> {
  key: string;
  header: string;
  /** Starting width in px; the user's own drag wins once they set one. */
  width?: number;
  align?: 'start' | 'end';
  sortable?: boolean;
  render: (row: T) => ReactNode;
}

export interface GridSort {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * A selection-aware grid that behaves the way a desktop application does:
 * click selects, double-click opens, right-click offers the row's actions,
 * and the arrow keys move without touching the mouse at all.
 *
 * Double-click and right-click are never the only route to anything — Enter
 * opens and the same actions sit on the screen's toolbar — because neither
 * gesture exists for a keyboard or a screen reader. A roving tabindex keeps
 * exactly one row in the tab order, so tabbing into the grid lands on the
 * selection rather than walking every row.
 */
export function DataGrid<T>({
  id,
  label,
  columns,
  rows,
  rowKey,
  selectedKey,
  onSelect,
  onOpen,
  openOnClick,
  onEdit,
  onDelete,
  actions,
  sort,
  onSortChange,
  empty,
  loading,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  onSelectRange,
  rowDepth,
  rowExpanded,
  onToggleExpand,
  rowSelectable,
  rowPartlySelected,
}: {
  id: string;
  label: string;
  columns: Array<GridColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  selectedKey: string | null;
  onSelect: (key: string, row: T) => void;
  onOpen?: (row: T) => void;
  /**
   * One click opens a row, for a list whose rows lead to their own screen.
   * Moving through the list with the keys still only selects, and coming back
   * lands on the row that was open.
   */
  openOnClick?: boolean;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  actions?: (row: T) => DesktopAction[];
  sort?: GridSort | undefined;
  onSortChange?: (sort: GridSort) => void;
  empty: string;
  loading?: boolean;
  /**
   * A checkbox column for bulk actions, separate from `selectedKey`'s single
   * "current row" — checking rows never changes which one the detail pane
   * shows. Present only when the caller passes all four of these.
   */
  selectedKeys?: ReadonlySet<string>;
  onToggleSelect?: (key: string) => void;
  onToggleSelectAll?: () => void;
  /**
   * Replaces the checked set outright — a plain click keeps just the row
   * clicked, a Shift-click keeps the contiguous run between the last row
   * touched and this one, the way File Explorer's own list does.
   */
  onSelectRange?: (keys: string[]) => void;
  /**
   * Rows as a tree, flattened by the caller: a child row follows its parent
   * in `rows` with a depth one greater. `rowExpanded` is true or false for a
   * row that can open and undefined for one that cannot; the first column
   * then carries the open/close arrow, and Right/Left open and close it.
   */
  rowDepth?: (row: T) => number;
  rowExpanded?: (row: T) => boolean | undefined;
  onToggleExpand?: (row: T) => void;
  /** False hides that row's checkbox — e.g. a group row standing for others. */
  rowSelectable?: (row: T) => boolean;
  /** True shows that row's checkbox half-ticked — a group with only some of its rows checked. */
  rowPartlySelected?: (row: T) => boolean;
}) {
  const widthsKey = `poultry.grid.${id}.widths`;
  const [widths, setWidths] = useState<Record<string, number>>(() => readStored(widthsKey, {}));
  const [menu, setMenu] = useState<{ x: number; y: number; row: T; copy: string } | null>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const resizing = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  // The last row a plain or Ctrl click touched — where a Shift-click's range
  // starts from. Reset to null whenever the row set changes so a stale index
  // from a previous page or filter can never anchor a range on this one.
  const anchorIndex = useRef<number | null>(null);
  useEffect(() => {
    anchorIndex.current = null;
  }, [rows]);
  const online = useConnection((s) => s.online);

  const selectedIndex = useMemo(
    () => rows.findIndex((r) => rowKey(r) === selectedKey),
    [rows, rowKey, selectedKey],
  );

  const focusRow = useCallback((index: number) => {
    const el = bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr')[index];
    el?.focus();
  }, []);

  const move = useCallback(
    (to: number) => {
      if (rows.length === 0) return;
      const index = Math.min(rows.length - 1, Math.max(0, to));
      const row = rows[index]!;
      onSelect(rowKey(row), row);
      focusRow(index);
    },
    [rows, rowKey, onSelect, focusRow],
  );

  useEffect(() => {
    if (openOnClick && selectedIndex >= 0) focusRow(selectedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Column resizing is a pointer drag on the header's right edge.
  useEffect(() => {
    function onMove(e: PointerEvent) {
      const state = resizing.current;
      if (!state) return;
      const next = Math.max(64, state.startWidth + (e.clientX - state.startX));
      setWidths((w) => ({ ...w, [state.key]: next }));
    }
    function onUp() {
      if (!resizing.current) return;
      resizing.current = null;
      document.body.classList.remove('is-resizing');
      setWidths((w) => {
        writeStored(widthsKey, w);
        return w;
      });
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [widthsKey]);

  // A menu anchored to a pointer position cannot follow the row, so anything
  // that moves the page closes it.
  useEffect(() => {
    if (!menu) return;
    function close() {
      setMenu(null);
    }
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('pointerdown', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('pointerdown', close);
    };
  }, [menu]);

  const depthOf = (r: T) => rowDepth?.(r) ?? 0;

  function onKeyDown(e: ReactKeyboardEvent<HTMLTableSectionElement>) {
    const row = selectedIndex >= 0 ? rows[selectedIndex] : undefined;
    switch (e.key) {
      // The tree keys, the way the navigator and File Explorer have them:
      // Right opens a closed row or steps into it, Left closes an open row or
      // steps back up to its parent.
      case 'ArrowRight': {
        if (!row || !onToggleExpand) return;
        const open = rowExpanded?.(row);
        if (open === false) onToggleExpand(row);
        else if (open && rows[selectedIndex + 1] && depthOf(rows[selectedIndex + 1]!) > depthOf(row))
          move(selectedIndex + 1);
        else return;
        break;
      }
      case 'ArrowLeft': {
        if (!row || !onToggleExpand) return;
        if (rowExpanded?.(row)) onToggleExpand(row);
        else if (depthOf(row) > 0) {
          let parent = selectedIndex - 1;
          while (parent >= 0 && depthOf(rows[parent]!) >= depthOf(row)) parent -= 1;
          if (parent < 0) return;
          move(parent);
        } else return;
        break;
      }
      case 'ArrowDown':
        move(selectedIndex + 1);
        break;
      case 'ArrowUp':
        move(selectedIndex - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(rows.length - 1);
        break;
      case 'PageDown':
        move(selectedIndex + 10);
        break;
      case 'PageUp':
        move(selectedIndex - 10);
        break;
      case 'Enter':
        if (row && onOpen) onOpen(row);
        break;
      case 'F2':
        if (row && onEdit) onEdit(row);
        break;
      case 'Delete':
        if (row && onDelete) onDelete(row);
        break;
      case 'Escape':
        setMenu(null);
        return;
      // Ctrl+C with nothing highlighted copies the whole row, cells
      // tab-separated so it pastes into a spreadsheet as one row.
      case 'c':
      case 'C': {
        if (!(e.ctrlKey || e.metaKey) || window.getSelection()?.toString()) return;
        const tr = (e.target as Element).closest('tr');
        if (!tr) return;
        const cells = [...tr.querySelectorAll<HTMLElement>('td:not(.grid__checkcol)')];
        copyText(cells.map((td) => (td.textContent ?? '').trim()).join('\t'));
        break;
      }
      default:
        return;
    }
    e.preventDefault();
  }

  // Offline, nothing loads until the line is back: said, rather than "Loading…" for ever (.docs/023).
  if (loading) return <p className="muted">{online ? 'Loading…' : NOT_ON_THIS_PC}</p>;
  if (rows.length === 0) return <p className="muted grid__empty">{empty}</p>;

  const bulkSelect = Boolean(selectedKeys && onToggleSelect && onToggleSelectAll && onSelectRange);
  const checkable = rowSelectable ? rows.filter(rowSelectable) : rows;
  const allChecked = bulkSelect && checkable.length > 0 && checkable.every((r) => selectedKeys!.has(rowKey(r)));
  const someChecked =
    bulkSelect &&
    !allChecked &&
    checkable.some((r) => selectedKeys!.has(rowKey(r)) || Boolean(rowPartlySelected?.(r)));
  const tree = Boolean(rowExpanded && onToggleExpand);

  return (
    <div className="grid__wrap">
      <table className="table grid" aria-label={label}>
        <thead>
          <tr>
            {bulkSelect && (
              <th className="grid__checkcol">
                <input
                  type="checkbox"
                  aria-label={someChecked || allChecked ? 'Clear selection' : 'Select all rows'}
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someChecked;
                  }}
                  onChange={() => onToggleSelectAll!()}
                />
              </th>
            )}
            {columns.map((c) => {
              const sorted = sort?.key === c.key ? sort.direction : undefined;
              return (
                <th
                  key={c.key}
                  style={widths[c.key] ? { width: widths[c.key] } : c.width ? { width: c.width } : undefined}
                  className={c.align === 'end' ? 'num' : undefined}
                  aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.sortable && onSortChange ? (
                    <button
                      type="button"
                      className="grid__sort"
                      onClick={() =>
                        onSortChange({
                          key: c.key,
                          direction: sorted === 'asc' ? 'desc' : 'asc',
                        })
                      }
                    >
                      {c.header}
                      <span aria-hidden="true">{sorted === 'asc' ? ' ▲' : sorted === 'desc' ? ' ▼' : ''}</span>
                    </button>
                  ) : (
                    c.header
                  )}
                  <span
                    className="grid__resizer"
                    role="presentation"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      resizing.current = {
                        key: c.key,
                        startX: e.clientX,
                        startWidth: widths[c.key] ?? c.width ?? 140,
                      };
                      document.body.classList.add('is-resizing');
                    }}
                  />
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody ref={bodyRef} onKeyDown={onKeyDown}>
          {rows.map((row, index) => {
            const key = rowKey(row);
            const selected = key === selectedKey;
            const depth = depthOf(row);
            const open = tree ? rowExpanded!(row) : undefined;
            const selectable = rowSelectable?.(row) ?? true;
            return (
              <tr
                key={key}
                className={`grid__row${selected ? ' grid__row--selected' : ''}${depth > 0 ? ' grid__row--child' : ''}${openOnClick ? ' grid__row--link' : ''}`}
                aria-selected={selected}
                aria-level={tree ? depth + 1 : undefined}
                aria-expanded={open}
                // Exactly one row is tabbable: the selected one, or the first
                // when nothing is selected yet.
                tabIndex={selected || (selectedIndex < 0 && index === 0) ? 0 : -1}
                // A Shift-click means "check this range," not "extend a text
                // selection" — stopped on mousedown, since that is what
                // actually starts the browser's own selection gesture.
                onMouseDown={(e) => {
                  if (e.shiftKey) e.preventDefault();
                }}
                onClick={(e) => {
                  onSelect(key, row);
                  // Dragging across a row to copy from it is not a click on it.
                  const plain = !e.shiftKey && !e.ctrlKey && !e.metaKey;
                  if (openOnClick && plain && !window.getSelection()?.toString()) onOpen?.(row);
                  // A row that opens on a click is opened, not ticked: the
                  // ticks stay as they were, for the checkbox and Ctrl/Shift.
                  if (!bulkSelect || (openOnClick && plain)) return;
                  if (e.shiftKey) {
                    const anchor = anchorIndex.current ?? index;
                    const [start, end] = anchor <= index ? [anchor, index] : [index, anchor];
                    const run = rows.slice(start, end + 1);
                    onSelectRange!((rowSelectable ? run.filter(rowSelectable) : run).map(rowKey));
                  } else if (e.ctrlKey || e.metaKey) {
                    if (selectable) onToggleSelect!(key);
                    anchorIndex.current = index;
                  } else {
                    onSelectRange!(selectable ? [key] : []);
                    anchorIndex.current = index;
                  }
                }}
                onFocus={() => {
                  if (!selected) onSelect(key, row);
                }}
                onDoubleClick={() => {
                  if (!openOnClick) onOpen?.(row);
                }}
                onContextMenu={(e) => {
                  if (!actions) return;
                  e.preventDefault();
                  onSelect(key, row);
                  // Our menu replaces the browser's, so it carries the Copy
                  // the browser's would have: the highlighted text, or else
                  // the cell that was right-clicked.
                  const selected = window.getSelection()?.toString().trim();
                  const cell = (e.target as Element).closest('td') as HTMLElement | null;
                  setMenu({ x: e.clientX, y: e.clientY, row, copy: selected || cell?.textContent?.trim() || '' });
                }}
              >
                {bulkSelect && (
                  <td className="grid__checkcol" onClick={(e) => e.stopPropagation()}>
                    {selectable && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${key}`}
                        checked={selectedKeys!.has(key)}
                        ref={(el) => {
                          if (el) el.indeterminate = Boolean(rowPartlySelected?.(row));
                        }}
                        onChange={() => {
                          onToggleSelect!(key);
                          anchorIndex.current = index;
                        }}
                        // A checkbox click moves focus into the row, which
                        // would otherwise make the row "current" the same way
                        // pressing Tab does — checking a row must never do
                        // that, so the focus event stops here.
                        onFocus={(e) => e.stopPropagation()}
                      />
                    )}
                  </td>
                )}
                {columns.map((c, ci) => (
                  <td key={c.key} className={c.align === 'end' ? 'num' : undefined}>
                    {tree && ci === 0 ? (
                      <span className="grid__treecell" style={{ paddingInlineStart: `${depth * 1.4}rem` }}>
                        {open !== undefined ? (
                          <button
                            type="button"
                            className="grid__twisty"
                            aria-label={open ? 'Collapse' : 'Expand'}
                            tabIndex={-1}
                            onClick={(e) => {
                              // Not the row's click: that would replace a bulk
                              // selection with this row.
                              e.stopPropagation();
                              onToggleExpand!(row);
                            }}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            {open ? '▾' : '▸'}
                          </button>
                        ) : (
                          <span className="grid__twisty" aria-hidden="true" />
                        )}
                        {c.render(row)}
                      </span>
                    ) : (
                      c.render(row)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {menu && actions && (
        <RowContextMenu
          x={menu.x}
          y={menu.y}
          actions={[
            ...(menu.copy
              ? [{ label: 'Copy', icon: 'copy' as const, shortcut: 'Ctrl+C', onSelect: () => copyText(menu.copy) }]
              : []),
            ...visibleActions(actions(menu.row)).map((a, i) =>
              i === 0 && menu.copy ? { ...a, separatorBefore: true } : a,
            ),
          ]}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

function RowContextMenu({
  x,
  y,
  actions,
  onClose,
}: {
  x: number;
  y: number;
  actions: DesktopAction[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus();
  }, []);

  if (actions.length === 0) return null;

  return (
    <div
      ref={ref}
      className="ctxmenu"
      role="menu"
      style={{ left: x, top: y }}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          role="menuitem"
          className={`ctxmenu__item${a.danger ? ' ctxmenu__item--danger' : ''}${
            a.separatorBefore ? ' ctxmenu__item--group' : ''
          }`}
          disabled={a.enabled === false}
          title={a.enabled === false ? a.reason : undefined}
          onClick={() => {
            a.onSelect();
            onClose();
          }}
        >
          <span className={a.icon ? 'withicon' : undefined}>
            {a.icon && <AppIcon kind={a.icon} size={14} />}
            {a.label}
          </span>
          {a.shortcut && <span className="ctxmenu__key">{a.shortcut}</span>}
        </button>
      ))}
    </div>
  );
}
